import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";

const ACTION_LABELS = {
  "auth.setup.completed": "Configuration initiale terminée",
  "auth.login.success": "Connexion réussie",
  "auth.login.failed": "Échec de connexion",
  "auth.lockout": "Compte temporairement verrouillé",
  "auth.logout": "Déconnexion",
  "auth.refresh": "Session renouvelée",
  "device.enrolled": "Appareil enrôlé",
  "device.enrollment.requested": "Demande d’enrôlement",
  "device.approved": "Appareil approuvé",
  "device.rejected": "Appareil refusé",
  "device.updated": "Configuration modifiée",
  "device.deleted": "Élément supprimé",
  "device.disabled": "Appareil désactivé",
  "device.enabled": "Appareil activé",
  "device.token.rotated": "Jeton renouvelé",
  "device.token.revoked": "Jeton révoqué",
  "device.connected": "Appareil connecté",
  "device.disconnected": "Appareil déconnecté",
  "device.heartbeat.lost": "Connexion appareil perdue",
  "device.capabilities.announced": "Capacités appareil reçues",
  "camera.ip.added": "Caméra IP ajoutée",
  "stream.started": "Flux démarré",
  "stream.stopped": "Flux arrêté",
  "system.error": "Erreur système",
};

const ACTION_TONES = {
  "auth.login.success": "text-emerald-400",
  "auth.login.failed": "text-red-400",
  "auth.lockout": "text-red-400",
  "device.deleted": "text-red-400",
  "camera.ip.added": "text-brand",
  "device.connected": "text-emerald-400",
  "stream.started": "text-emerald-400",
  "device.disabled": "text-amber-300",
  "device.disconnected": "text-zinc-400",
  "stream.stopped": "text-zinc-400",
  "system.error": "text-red-400",
};

function actorLabel(entry) {
  if (entry.actor_type === "system") return "Système";
  if (entry.actor_type === "agent") return "Agent";
  return "Utilisateur";
}

function targetLabel(entry) {
  if (!entry.target_type) return "—";
  const names = { camera: "Caméra", device: "Appareil", user: "Utilisateur" };
  const name = names[entry.target_type] || entry.target_type;
  return entry.target_id ? `${name} · ${String(entry.target_id).slice(0, 8)}` : name;
}

export default function AuditPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/audit?limit=200");
      setEntries(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-page">
      <PageHeader
        title="Journal"
        description="Historique des opérations importantes réalisées dans CAM."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="size-11 grid place-items-center rounded-xl border border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.05] disabled:opacity-40 transition-colors"
              data-testid="audit-refresh"
              aria-label="Actualiser le journal"
              title="Actualiser"
            >
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.7} />
            </button>
            <Link
              to="/settings"
              className="h-11 inline-flex items-center gap-2 rounded-xl border border-white/[0.08] px-3.5 text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/[0.05] transition-colors"
              data-testid="audit-back-settings"
            >
              <ChevronLeft className="size-4" strokeWidth={1.7} />
              Paramètres
            </Link>
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-10">
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden">
          {loading && entries.length === 0 ? (
            <div className="px-5 py-10 text-sm text-zinc-500 animate-pulse" data-testid="audit-loading">Chargement du journal…</div>
          ) : entries.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <div className="text-sm font-medium text-zinc-300">Aucune activité enregistrée</div>
              <div className="text-xs text-zinc-500 mt-1">Les opérations importantes apparaîtront ici.</div>
            </div>
          ) : (
            <>
              <div className="divide-y divide-white/[0.06] sm:hidden" data-testid="audit-mobile-list">
                {entries.map((entry) => (
                  <article key={entry.id} className="px-4 py-4" data-testid={`audit-mobile-row-${entry.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className={`min-w-0 text-sm font-medium ${ACTION_TONES[entry.action] || "text-zinc-200"}`}>
                        {ACTION_LABELS[entry.action] || entry.action}
                      </div>
                      <div className="shrink-0 text-[11px] text-zinc-500">{dayjs(entry.timestamp).format("DD/MM HH:mm")}</div>
                    </div>
                    <div className="mt-2 grid gap-1 text-xs text-zinc-500">
                      <div><span className="text-zinc-600">Origine :</span> {actorLabel(entry)}</div>
                      <div><span className="text-zinc-600">Cible :</span> {targetLabel(entry)}</div>
                      <div><span className="text-zinc-600">IP :</span> {entry.ip || "—"}</div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.07] text-xs text-zinc-500">
                      <th className="text-left font-medium px-4 py-3">Date</th>
                      <th className="text-left font-medium px-4 py-3">Événement</th>
                      <th className="text-left font-medium px-4 py-3">Origine</th>
                      <th className="text-left font-medium px-4 py-3">Cible</th>
                      <th className="text-left font-medium px-4 py-3">Adresse IP</th>
                    </tr>
                  </thead>
                  <tbody data-testid="audit-table">
                    {entries.map((entry) => (
                      <tr key={entry.id} className="border-b border-white/[0.05] last:border-0 hover:bg-white/[0.025] transition-colors" data-testid={`audit-row-${entry.id}`}>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{dayjs(entry.timestamp).format("DD/MM/YYYY HH:mm:ss")}</td>
                        <td className={`px-4 py-3 font-medium ${ACTION_TONES[entry.action] || "text-zinc-200"}`}>
                          <div>{ACTION_LABELS[entry.action] || entry.action}</div>
                          {!ACTION_LABELS[entry.action] && <div className="text-[11px] font-normal text-zinc-600 mt-0.5">Événement technique</div>}
                        </td>
                        <td className="px-4 py-3 text-zinc-400">{actorLabel(entry)}</td>
                        <td className="px-4 py-3 text-zinc-400">{targetLabel(entry)}</td>
                        <td className="px-4 py-3 text-zinc-500">{entry.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
